"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinearRgbColor = exports.fromRgb = void 0;
const fromRgb = (r, g, b, a, pivot = 0.03928 /* GAMMA_PIVOT.WCAG */) => {
    const lr = r <= pivot ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
    const lg = g <= pivot ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
    const lb = b <= pivot ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
    return new LinearRgbColor(lr, lg, lb, a);
};
exports.fromRgb = fromRgb;
class LinearRgbColor {
    static fromRgb({ r, g, b, a }, pivot) {
        return (0, exports.fromRgb)(r, g, b, a, pivot);
    }
    constructor(r, g, b, 
    /** Float in range 0 to 1. */
    a = 1) {
        this.r = r;
        this.g = g;
        this.b = b;
        this.a = a;
    }
    /** Luminance of the color, in range 0 to 1. Used for contrast ratio calculations. */
    Y() {
        return 0.2126 * this.r + 0.7152 * this.g + 0.0722 * this.b;
    }
    isLight() {
        return this.Y() > 0.179;
    }
    contrast(other) {
        let L1 = this.Y();
        let L2 = other.Y();
        if (L1 < L2)
            [L1, L2] = [L2, L1];
        return (L1 + 0.05) / (L2 + 0.05);
    }
    /**
     * @param background The background color.
     * @param threshold Desired contrast threshold.
     * @returns Returns a contrasty enough text color given a background color.
     */
    adjToContrast(background, threshold = 4.5) {
        const currentContrast = this.contrast(background);
        if (currentContrast >= threshold)
            return this;
        const bgY = background.Y();
        const fgY = this.Y();
        let targetY;
        if (fgY > bgY) {
            targetY = threshold * (bgY + 0.05) - 0.05;
            if (targetY > 1)
                targetY = (bgY + 0.05) / threshold - 0.05;
        }
        else {
            targetY = (bgY + 0.05) / threshold - 0.05;
            if (targetY < 0)
                targetY = threshold * (bgY + 0.05) - 0.05;
        }
        targetY = Math.max(0, Math.min(1, targetY));
        const ratio = targetY / Math.max(fgY, 0.0001);
        return new LinearRgbColor(Math.max(0, Math.min(this.r * ratio, 1)), Math.max(0, Math.min(this.g * ratio, 1)), Math.max(0, Math.min(this.b * ratio, 1)), this.a);
    }
    highestContrast(colors) {
        return this.pickFirstAboveOrMax(100, colors);
    }
    pickFirstAboveOrMax(threshold, colors) {
        let bestContrast = -1;
        let bestColor = colors[0];
        for (const color of colors) {
            const contrast = this.contrast(color);
            if (contrast > bestContrast) {
                bestContrast = contrast;
                bestColor = color;
            }
            if (contrast >= threshold)
                return color;
        }
        return bestColor;
    }
    toSrgbChannel(c) {
        return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    }
    toString() {
        const r = Math.round(this.toSrgbChannel(this.r) * 255);
        const g = Math.round(this.toSrgbChannel(this.g) * 255);
        const b = Math.round(this.toSrgbChannel(this.b) * 255);
        return this.a === 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${this.a})`;
    }
}
exports.LinearRgbColor = LinearRgbColor;
